import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  DatePicker,
  Typography,
  Space,
  Input,
  Button,
  Select,
  Tag,
  Table,
  Grid,
  App,
  Empty,
  type TableColumnsType,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useUrlState, oneOf, intParam, dayParam } from '../../lib/urlState';
import { useDebounced } from '../../lib/useDebounced';
import { QueryError } from '../../lib/QueryState';
import { userLabel, userSecondary } from '../../lib/listenerName';
import { usePermissions } from '../../auth/store';
import { Can } from '../../rbac/RequirePermission';
import { MasterDetail } from '../../lib/MasterDetail';
import { UserDetailView, type UserListItem } from './UserDetailView';

/** Rows fetched per request. The API caps `limit` at 100. */
/** Rows per API request — the API's maximum, so deep pages need few requests. */
const USERS_PAGE_SIZE = 100;
/** Rows per table page. */
const TABLE_PAGE_SIZE = 25;

interface UserListPage {
  items: UserListItem[];
  nextCursor: string | null;
  /** Every user in the table, ignoring the search box. */
  total: number;
  /** Users matching the current search. Equals `total` when not searching. */
  filtered: number;
}

type OnboardingFilter = 'completed' | 'incomplete';
type GenderFilter = 'male' | 'female' | 'nonbinary' | 'undisclosed';
type StatusFilter = 'active' | 'blocked' | 'deleted';

const GENDERS: readonly GenderFilter[] = ['male', 'female', 'nonbinary', 'undisclosed'];
const STATUSES: readonly StatusFilter[] = ['active', 'blocked', 'deleted'];

const LANGUAGE_OPTIONS = [
  { value: 'ta', label: 'Tamil' },
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'te', label: 'Telugu' },
  { value: 'kn', label: 'Kannada' },
  { value: 'ml', label: 'Malayalam' },
];

export function UsersPage() {
  const { message } = App.useApp();
  // Every filter, the search and the open drawer live in the URL (lib/urlState):
  // Back from a user's recharge returns to the same list; a refresh keeps it.
  //   ?user=   the open drawer (pushed, so the browser Back closes it)
  //   ?q=      @handle or phone last-4      ?gender= male|female|…|all
  //   ?status= ?language= ?onboarding= ?step=   ?joinedFrom= ?joinedTo= (YYYY-MM-DD)
  const { get, set } = useUrlState();
  const selectedId = get('user');
  const q = get('q') ?? '';
  const [qInput, setQInput] = useState(q);
  const qDebounced = useDebounced(qInput.trim(), 400);
  useEffect(() => {
    if (qDebounced !== q) set({ q: qDebounced, page: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);
  useEffect(() => setQInput(q), [q]);
  const onboarding = oneOf(get('onboarding'), ['completed', 'incomplete'] as const);
  const step = get('step') !== null ? intParam(get('step'), 0) : undefined;
  // Users = the callers, who are men (women become listeners on sign-up), so the
  // page opens on Male. `?gender=all` and the dashboard's registration
  // drill-downs — whose counts cover every gender — open unfiltered.
  const genderParam = get('gender');
  const gender: GenderFilter | undefined =
    oneOf(genderParam, GENDERS) ?? (genderParam === 'all' || onboarding ? undefined : 'male');
  const status = oneOf(get('status'), STATUSES);
  const language = get('language') ?? undefined;
  const joinedFromDay = dayParam(get('joinedFrom'));
  const joinedToDay = dayParam(get('joinedTo'));
  // Joined between these two local days (inclusive), as ISO instants.
  const joined =
    joinedFromDay && joinedToDay
      ? {
          from: new Date(`${joinedFromDay}T00:00:00`).toISOString(),
          // The API's upper bound is exclusive.
          to: new Date(new Date(`${joinedToDay}T00:00:00`).getTime() + 86_400_000).toISOString(),
        }
      : null;
  const openUser = (id: string) => set({ user: id }, { push: true });
  const closeUser = () => set({ user: null });
  // Master–detail: on a wide screen the selected user's detail opens beside the
  // list (lib/MasterDetail), and the list drops its secondary columns to make
  // room. Below `lg` the detail is a full-width drawer and the table is whole.
  const inline = Grid.useBreakpoint().lg === true;
  const compact = inline && !!selectedId;
  const stepFilter = onboarding === 'incomplete' ? step : undefined;
  /** Everything except the search box — sent to both the list and the export. */
  const filterParams = {
    onboarding,
    onboardingStep: stepFilter,
    gender,
    status,
    language,
    joinedFrom: joined?.from,
    joinedTo: joined?.to,
  };
  const isFiltered = Object.values(filterParams).some((v) => v !== undefined);
  const canExport = usePermissions().can('users.export');

  // Cursor-paged, NOT a single capped fetch. This previously asked for
  // `limit: 50` once and never read `nextCursor`, so the page could never show
  // more than 50 users however many existed. Same shape as the support inbox
  // (`pages/support/TicketList.tsx`), which is the established pattern here.
  const search = useInfiniteQuery({
    queryKey: ['admin', 'users', q, filterParams],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<UserListPage>('/admin/users', {
          params: {
            q: q || undefined,
            ...filterParams,
            cursor: pageParam,
            limit: USERS_PAGE_SIZE,
          },
        })
      ).data,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // Users can be created while we page, which shifts rows between pages, so
  // drop repeats rather than rendering a user twice.
  const rows = useMemo(() => {
    const seen = new Set<string>();
    const out: UserListItem[] = [];
    for (const page of search.data?.pages ?? []) {
      for (const u of page.items) {
        if (seen.has(u.id)) continue;
        seen.add(u.id);
        out.push(u);
      }
    }
    return out;
  }, [search.data]);

  // Counts come from the first page; every page carries the same figures.
  const counts = search.data?.pages[0];
  const isSearching = q.trim().length > 0;

  // Table page (?page=). The pager is sized from the SERVER's count of matching
  // users, not from the rows loaded so far — it used to show only the pages of
  // the first fetched batch (2 pages) and hide the rest behind "Load more".
  // Opening a later page fetches further batches until its rows are loaded.
  const page = intParam(get('page'), 1);
  const totalRows = counts ? counts.filtered : rows.length;
  const rowsNeeded = page * TABLE_PAGE_SIZE;
  const loadingPage = rows.length < rowsNeeded && search.hasNextPage;
  useEffect(() => {
    if (loadingPage && !search.isFetchingNextPage && !search.isError) void search.fetchNextPage();
  }, [loadingPage, search.isFetchingNextPage, search.isError, search]);
  // A page past the end (e.g. after a filter shrank the list) goes back to the last one.
  const lastPage = Math.max(1, Math.ceil(totalRows / TABLE_PAGE_SIZE));
  useEffect(() => {
    if (counts && page > lastPage) set({ page: lastPage === 1 ? null : lastPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts, page, lastPage]);


  const [exporting, setExporting] = useState(false);

  /**
   * Download the user spreadsheet.
   *
   * The file the API returns contains FULL phone numbers, so it is deliberately
   * NOT cached and not held in React state — it goes straight from the response to
   * a download and the object URL is revoked immediately.
   *
   * Scope matches what the operator is looking at: the current search and
   * filters, not the whole table. The button label says so, because "Export"
   * over a filtered view is otherwise ambiguous.
   */
  async function downloadUsersCsv() {
    setExporting(true);
    try {
      const res = await api.get('/admin/users/export', {
        params: { q: q.trim() || undefined, ...filterParams },
        responseType: 'blob',
      });
      const blob = new Blob([res.data as BlobPart], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = q.trim() || isFiltered ? `users-${stamp}-filtered.csv` : `users-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Could not download the user export');
    } finally {
      setExporting(false);
    }
  }

  const columns: TableColumnsType<UserListItem> = [
    {
      title: 'User',
      dataIndex: 'handle',
      render: (_h: string, row) => {
        const sub = userSecondary(row);
        return (
          <Space size={6}>
            <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <a onClick={() => openUser(row.id)} style={{ fontWeight: sub ? 600 : undefined }}>
                {userLabel(row)}
              </a>
              {sub ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {sub}
                </Typography.Text>
              ) : null}
            </span>
            {row.listener ? <Tag color="purple">listener</Tag> : null}
          </Space>
        );
      },
    },
    {
      title: 'Phone',
      dataIndex: 'phoneLast4',
      width: 170,
      // The full number for admins allowed to see it; the last four otherwise.
      render: (last4: string, row) =>
        row.phone ? (
          <Typography.Text copyable style={{ whiteSpace: 'nowrap' }}>
            {row.phone}
          </Typography.Text>
        ) : (
          `••• ${last4}`
        ),
    },
    ...(compact
      ? []
      : ([
          { title: 'Language', dataIndex: 'languagePref', width: 70 },
          {
            title: 'Gender',
            dataIndex: 'gender',
            render: (g: string | null) => g ?? '—',
            width: 110,
          },
          {
            title: 'Registration',
            dataIndex: 'onboardingLabel',
            width: 150,
            render: (label: string, row) =>
              row.onboardingCompleted ? (
                <Tag color="success">Completed</Tag>
              ) : (
                <Tag color="warning">{label}</Tag>
              ),
          },
        ] as TableColumnsType<UserListItem>)),
    {
      title: 'Status',
      width: 100,
      render: (_: unknown, row) =>
        row.deletedAt ? (
          <Tag color="error">Deleted</Tag>
        ) : row.blockedAt ? (
          <Tag color="volcano">Blocked</Tag>
        ) : (
          <Tag color="success">Active</Tag>
        ),
    },
    {
      title: 'Calls',
      dataIndex: 'totalCalls',
      width: 80,
      render: (n: number | undefined) => (n ?? 0).toLocaleString('en-IN'),
    },
    ...(compact
      ? []
      : ([
          {
            title: 'Joined',
            dataIndex: 'createdAt',
            render: (s: string) => new Date(s).toLocaleString(),
            width: 190,
          },
          {
            title: '',
            render: (_: unknown, row) => (
              <Can permission="users.detail.view">
                <Button size="small" onClick={() => openUser(row.id)}>
                  Open
                </Button>
              </Can>
            ),
          },
        ] as TableColumnsType<UserListItem>)),
  ];

  // Search, filters, counts and the table — the left pane of the master–detail.
  const listCard = (
  <Card>
    <Space wrap style={{ width: '100%', marginBottom: 16 }}>
      <Input.Search
        placeholder="Search by name, @handle or phone last-4 digits"
        allowClear
        enterButton
        aria-label="Search users"
        value={qInput}
        onChange={(e) => setQInput(e.target.value)}
        onSearch={(v) => set({ q: v.trim(), page: null })}
        style={{ minWidth: 320 }}
      />
      <Select<GenderFilter | 'all'>
        aria-label="Gender filter"
        style={{ minWidth: 150 }}
        value={gender ?? 'all'}
        onChange={(v) => set({ gender: v, page: null })}
        options={[
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
          { value: 'nonbinary', label: 'Non-binary' },
          { value: 'undisclosed', label: 'Gender not set' },
          { value: 'all', label: 'All genders' },
        ]}
      />
      <Select<StatusFilter | undefined>
        allowClear
        aria-label="Status filter"
        placeholder="Status: all"
        style={{ minWidth: 140 }}
        value={status}
        onChange={(v) => set({ status: v ?? null, page: null })}
        options={[
          { value: 'active', label: 'Active' },
          { value: 'blocked', label: 'Blocked' },
          { value: 'deleted', label: 'Deleted' },
        ]}
      />
      <Select<string | undefined>
        allowClear
        aria-label="Language filter"
        placeholder="Language: all"
        style={{ minWidth: 150 }}
        value={language}
        onChange={(v) => set({ language: v ?? null, page: null })}
        options={LANGUAGE_OPTIONS}
      />
      <DatePicker.RangePicker
        aria-label="Joined between"
        placeholder={['Joined from', 'Joined to']}
        key={`${joinedFromDay}|${joinedToDay}`}
        onChange={(_dates, [f, t]) => set({ joinedFrom: f || null, joinedTo: t || null, page: null })}
      />
      {joined && (
        <Tag closable color="blue" onClose={() => set({ joinedFrom: null, joinedTo: null, page: null })}>
          Joined {joinedFromDay} → {joinedToDay}
        </Tag>
      )}
      <Select<OnboardingFilter | undefined>
        allowClear
        aria-label="Registration filter"
        placeholder="Registration: all"
        style={{ minWidth: 220 }}
        value={onboarding}
        onChange={(v) => set({ onboarding: v ?? null, step: null, page: null })}
        options={[
          { value: 'completed', label: 'Registration completed' },
          { value: 'incomplete', label: 'Registration incomplete' },
        ]}
      />
      {stepFilter !== undefined && (
        <Tag closable color="blue" onClose={() => set({ step: null, page: null })}>
          Stopped at step {stepFilter + 1}
        </Tag>
      )}
      {canExport && (
        <Button
          icon={<DownloadOutlined />}
          loading={exporting}
          onClick={() => void downloadUsersCsv()}
        >
          {isSearching || isFiltered ? 'Export matching' : 'Export all'}
        </Button>
      )}
    </Space>
    {/* The real population, not the size of one page. When a search is
        active both numbers are shown, because "Total" alone next to a
        filtered table reads as though rows are missing. */}
    {counts && (
      <Space size={8} style={{ marginBottom: 12 }}>
        <Tag color="blue">Total users: {counts.total.toLocaleString('en-IN')}</Tag>
        {(isSearching || isFiltered) && (
          <Tag color="geekblue">
            {isSearching ? `Matching “${q.trim()}”` : 'Matching filter'}:{' '}
            {counts.filtered.toLocaleString('en-IN')}
          </Tag>
        )}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Showing {rows.length.toLocaleString('en-IN')}
        </Typography.Text>
      </Space>
    )}
    {search.isError && !search.data ? (
      <QueryError error={search.error} what="users" onRetry={() => void search.refetch()} />
    ) : search.data && rows.length === 0 ? (
      <Empty description={isSearching || isFiltered ? 'No users match this search or filter.' : 'No users yet.'} />
    ) : (
      <>
        <Table
          rowKey="id"
          loading={search.isLoading || loadingPage}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize: TABLE_PAGE_SIZE,
            total: Math.max(totalRows, rows.length),
            showSizeChanger: false,
            showQuickJumper: totalRows > TABLE_PAGE_SIZE * 5,
            showTotal: (t, [from, to]) => `${from}–${to} of ${t.toLocaleString('en-IN')}`,
            onChange: (p) => set({ page: p === 1 ? null : p }),
          }}
          // The whole row selects the user. Links, buttons and the copy icon
          // inside it act on their own (the name link already opens it).
          onRow={(row) => ({
            onClick: (e) => {
              if ((e.target as HTMLElement).closest('a, button, .ant-typography-copy')) return;
              if (row.id !== selectedId) openUser(row.id);
            },
          })}
          rowClassName={(row) =>
            row.id === selectedId ? 'master-detail-row master-detail-row-selected' : 'master-detail-row'
          }
        />
      </>
    )}
  </Card>
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Users
      </Typography.Title>
      <MasterDetail
        open={!!selectedId}
        onClose={closeUser}
        chrome={false}
        list={listCard}
      >
        {selectedId && <UserDetailView id={selectedId} onClose={closeUser} />}
      </MasterDetail>
    </Space>
  );
}
